import {useState, type KeyboardEvent, type ReactNode} from "react";
import {ChevronRightIcon} from "@heroicons/react/24/outline";
import {cn, formatDate} from "../../../../../../lib/utils.ts";
import type {CurrencyEnum} from "../../../../../../generated/enums.ts";
import type {DocumentSettlement} from "../../../types/types.ts";
import {SettlementStatus} from "../BookingGroupBox.tsx";
import {TypeBadge} from "../TypeBadge.tsx";
import type {PanelBooking} from "../../types.ts";
import {
    groupDocumentsByRelation,
    sortByVatRate,
    type DocumentSummary,
    type SortableVatRow,
    type VatRow,
    type VatRowSort,
    type VatRowSortColumn,
} from "../../utils/invoiceVatRows.ts";
import {VatLabelBadge} from "./VatLabelBadge.tsx";
import {
    AmountText,
    INDENT_REM,
    NestedBookings,
    RelationCell,
    VatRowHeader,
    gridTemplate,
    type SelectAllState,
} from "./VatTableParts.tsx";

interface InvoiceVatGroupsTableProps {
    documents: DocumentSummary[];
    groupByRelation: boolean;
    selectAll: SelectAllState;
    currency: CurrencyEnum;
    sort: VatRowSort;
    onSortChange: (column: VatRowSortColumn) => void;
    expandedKeys: ReadonlySet<string>;
    onToggleRow: (key: string) => void;
    showStatus: boolean;
    settlementFor: (billId: string | null) => DocumentSettlement | null;
    renderBookings: (bookings: PanelBooking[]) => ReactNode;
}

function activateOnKey(handler: () => void) {
    return (event: KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handler();
        }
    };
}

function indent(level: number): string {
    return `${level * INDENT_REM}rem`;
}

function Chevron({expanded, level}: {expanded: boolean; level: number}) {
    return (
        <ChevronRightIcon
            className={cn("size-4 text-muted-foreground transition-transform", expanded && "rotate-90")}
            style={{marginLeft: indent(level)}}
            aria-hidden
        />
    );
}

function Amounts({row}: {row: SortableVatRow}) {
    return (
        <>
            <span className="justify-self-end whitespace-nowrap px-2">
                <AmountText amount={row.debit} />
            </span>
            <span className="justify-self-end whitespace-nowrap px-2">
                <AmountText amount={row.credit} />
            </span>
        </>
    );
}

function RateChips({parts}: {parts: VatRow[]}) {
    const known = parts.filter((part) => part.vatKnown);
    if (known.length === 0) return <span className="pl-2 pr-4 text-muted-foreground">-</span>;
    return (
        <span className="flex flex-wrap gap-1 pl-2 pr-4">
            {known.map((part) => (
                <VatLabelBadge key={part.key} label={part.vatLabel} size="xs" />
            ))}
        </span>
    );
}

function NestedDocumentLabel({document}: {document: DocumentSummary}) {
    const isInvoice = document.kind === "BILL";
    const showTitle = document.title !== document.relationName;
    return (
        <span className="flex min-w-0 items-center gap-2">
            {!isInvoice && <TypeBadge kind={document.kind} />}
            {document.invoiceNumber && (
                <span className="min-w-0 tabular-nums text-gray-800 [overflow-wrap:anywhere]">{document.invoiceNumber}</span>
            )}
            {showTitle && <span className="truncate text-sm text-muted-foreground">{document.title}</span>}
        </span>
    );
}

function lineCountLabel(count: number): string {
    return `${count} ${count === 1 ? "regel" : "regels"}`;
}

const ROW_CLASS =
    "grid cursor-pointer items-center py-2.5 pl-3 text-sm hover:bg-muted focus:outline-none focus-visible:bg-muted";

export function InvoiceVatGroupsTable({
    documents,
    groupByRelation,
    selectAll,
    currency,
    sort,
    onSortChange,
    expandedKeys,
    onToggleRow,
    showStatus,
    settlementFor,
    renderBookings,
}: InvoiceVatGroupsTableProps) {
    const [collapsedRelations, setCollapsedRelations] = useState<Set<string>>(new Set());

    function toggleRelation(key: string) {
        setCollapsedRelations((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    function rateRow(part: VatRow, level: number) {
        const expanded = expandedKeys.has(part.key);
        return (
            <div key={part.key} className="border-t border-gray-100">
                <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={expanded}
                    onClick={() => onToggleRow(part.key)}
                    onKeyDown={activateOnKey(() => onToggleRow(part.key))}
                    className={ROW_CLASS}
                    style={gridTemplate(showStatus, !groupByRelation)}
                >
                    <Chevron expanded={expanded} level={level} />
                    <span className="px-2 text-xs text-muted-foreground" style={{paddingLeft: indent(level)}}>
                        {lineCountLabel(part.bookings.length)}
                    </span>
                    {!groupByRelation && <span />}
                    <span />
                    <Amounts row={part} />
                    <span className="pl-2 pr-4">
                        {part.vatKnown ? (
                            <VatLabelBadge label={part.vatLabel} size="xs" />
                        ) : (
                            <span className="text-muted-foreground">{part.vatLabel}</span>
                        )}
                    </span>
                    {showStatus && <span />}
                </div>
                {expanded && <NestedBookings showStatus={showStatus}>{renderBookings(part.bookings)}</NestedBookings>}
            </div>
        );
    }

    function documentRow(document: DocumentSummary, level: number) {
        const documentKey = `doc:${document.key}`;
        const expanded = expandedKeys.has(documentKey);
        const settlement = settlementFor(document.billId);
        const nested = level > 0;
        return (
            <div key={document.key} className="border-t border-gray-100 first:border-t-0">
                <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={expanded}
                    onClick={() => onToggleRow(documentKey)}
                    onKeyDown={activateOnKey(() => onToggleRow(documentKey))}
                    className={ROW_CLASS}
                    style={gridTemplate(showStatus, !groupByRelation)}
                >
                    <Chevron expanded={expanded} level={level} />
                    <span className="min-w-0 px-2" style={{paddingLeft: nested ? indent(level) : undefined}}>
                        {nested ? <NestedDocumentLabel document={document} /> : <RelationCell row={document} />}
                    </span>
                    {!nested && (
                        <span className="px-2 tabular-nums text-gray-800 [overflow-wrap:anywhere]">
                            {document.invoiceNumber ?? <span className="text-muted-foreground">-</span>}
                        </span>
                    )}
                    <span className="whitespace-nowrap px-2 tabular-nums text-gray-800">
                        {formatDate(document.date) || "-"}
                    </span>
                    <Amounts row={document} />
                    <RateChips parts={document.parts} />
                    {showStatus && (
                        <span className="flex justify-end pr-3">
                            {settlement && <SettlementStatus settlement={settlement} />}
                        </span>
                    )}
                </div>
                {expanded && document.parts.map((part) => rateRow(part, level + 1))}
            </div>
        );
    }

    const relations = groupByRelation ? groupDocumentsByRelation(documents, currency) : [];

    return (
        <div className="flex flex-col">
            <div className="rounded-lg border border-gray-200 bg-white">
                <VatRowHeader
                    sort={sort}
                    onSortChange={onSortChange}
                    showStatus={showStatus}
                    showInvoiceColumn={!groupByRelation}
                    selectAll={selectAll}
                />
                {!groupByRelation && <div>{documents.map((document) => documentRow(document, 0))}</div>}
                {groupByRelation &&
                    relations.map((relation) => {
                        const relationExpanded = !collapsedRelations.has(relation.key);
                        const relationParts = relation.documents.flatMap((document) => document.parts);
                        const rateLabels = sortByVatRate([
                            ...new Map(relationParts.map((part) => [part.vatLabel, part])).values(),
                        ]);
                        return (
                            <div key={relation.key} className="border-b border-gray-200 last:border-b-0">
                                <div
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={relationExpanded}
                                    onClick={() => toggleRelation(relation.key)}
                                    onKeyDown={activateOnKey(() => toggleRelation(relation.key))}
                                    className={cn(ROW_CLASS, "bg-gray-50")}
                                    style={gridTemplate(showStatus, !groupByRelation)}
                                >
                                    <Chevron expanded={relationExpanded} level={0} />
                                    <span className="min-w-0 px-2">
                                        <span className="block truncate font-semibold text-foreground">
                                            {relation.relationName}
                                        </span>
                                        <span className="block text-xs text-muted-foreground">
                                            {relation.documents.length}{" "}
                                            {relation.documents.length === 1 ? "factuur" : "facturen"}
                                        </span>
                                    </span>
                                    <span />
                                    <Amounts row={relation} />
                                    <RateChips parts={rateLabels} />
                                    {showStatus && <span />}
                                </div>
                                {relationExpanded && (
                                    <div className="border-t border-gray-100">
                                        {relation.documents.map((document) => documentRow(document, 1))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </div>
        </div>
    );
}
